/**
 * Source Tracking — First-Touch Attribution
 *
 * Captures the very first traffic source when a user visits any page.
 * Uses localStorage so the data persists across sessions until the user
 * signs up (or clears their browser data).
 *
 * This module complements adTracking.js (which only captures Facebook ads)
 * by also detecting organic, referral, affiliate, and direct sources.
 */

const FIRST_TOUCH_KEY = "aw_first_touch";

/**
 * Detect the source category from URL params and document.referrer.
 * Returns a human-readable label used in Telegram notifications and DB.
 *
 * @param {URLSearchParams} urlParams
 * @param {string} referrer - document.referrer
 * @returns {string}
 */
function detectSourceCategory(urlParams, referrer) {
  // 1. Paid ads (highest priority — explicit click IDs)
  if (urlParams.get("fbclid")) return "facebook_ads";
  if (urlParams.get("gclid")) return "google_ads";
  if (urlParams.get("ttclid")) return "tiktok_ads";

  // 2. Affiliate
  if (urlParams.get("ref")) return "affiliate";

  // 3. UTM-tagged links (campaigns, newsletters, etc.)
  const utmSource = urlParams.get("utm_source");
  if (utmSource) return `utm:${utmSource}`;

  // 4. Referrer-based detection (organic / social)
  if (referrer) {
    try {
      const host = new URL(referrer).hostname.toLowerCase();

      if (host.includes("facebook.com") || host.includes("fb.com") || host.includes("m.facebook.com"))
        return "facebook_organic";
      if (host.includes("google."))
        return "google_organic";
      if (host.includes("zalo"))
        return "zalo";
      if (host.includes("tiktok.com"))
        return "tiktok_organic";
      if (host.includes("youtube.com") || host.includes("youtu.be"))
        return "youtube";
      if (host.includes("t.me") || host.includes("telegram"))
        return "telegram";
      if (host.includes("instagram.com"))
        return "instagram";
      if (host.includes("threads.net"))
        return "threads";
      if (host.includes("twitter.com") || host.includes("x.com"))
        return "twitter";

      // Generic referral from another website
      return "referral";
    } catch {
      return "referral";
    }
  }

  // 5. No referrer, no params → direct traffic
  return "direct";
}

/**
 * Capture first-touch source data and persist to localStorage.
 * Only writes ONCE — subsequent calls are no-ops so the original
 * attribution is preserved (first-touch model).
 *
 * Should be called as early as possible (App.js useEffect, LandingPage mount).
 *
 * @returns {Object|null} The captured source data, or null if already captured.
 */
export const captureFirstTouch = () => {
  try {
    // First-touch: never overwrite
    if (localStorage.getItem(FIRST_TOUCH_KEY)) return null;

    const urlParams = new URLSearchParams(window.location.search);
    const referrer = document.referrer || "";

    const firstTouch = {
      timestamp: Date.now(),
      landingPage: window.location.pathname + window.location.search,
      referrer: referrer || null,
      sourceCategory: detectSourceCategory(urlParams, referrer),

      // Ad click IDs
      fbclid: urlParams.get("fbclid") || null,
      gclid: urlParams.get("gclid") || null,
      ttclid: urlParams.get("ttclid") || null,

      // Affiliate
      ref: urlParams.get("ref") || null,

      // UTM params
      utm_source: urlParams.get("utm_source") || null,
      utm_medium: urlParams.get("utm_medium") || null,
      utm_campaign: urlParams.get("utm_campaign") || null,
      utm_content: urlParams.get("utm_content") || null,
      utm_term: urlParams.get("utm_term") || null,
    };

    // Strip null values for cleaner storage
    const cleaned = Object.fromEntries(
      Object.entries(firstTouch).filter(([, v]) => v !== null)
    );

    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(cleaned));
    console.log("📌 First-touch source captured:", cleaned.sourceCategory);
    return cleaned;
  } catch (error) {
    console.error("Error capturing first-touch source:", error);
    return null;
  }
};

/**
 * Retrieve stored first-touch source data.
 * @returns {Object} The stored source data or empty object.
 */
export const getFirstTouch = () => {
  try {
    const stored = localStorage.getItem(FIRST_TOUCH_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
};

/**
 * Clear stored first-touch data (e.g. after successful registration).
 */
export const clearFirstTouch = () => {
  try {
    localStorage.removeItem(FIRST_TOUCH_KEY);
  } catch (error) {
    console.error("Error clearing first-touch data:", error);
  }
};

export default {
  captureFirstTouch,
  getFirstTouch,
  clearFirstTouch,
};
